import { Box, Chip, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { ImageBox } from '@tix-ticketing/ui-components';
import { ClockIcon } from '@tix-ticketing/ui-icons/outline';
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl';
import type { SpecialOfferResponse } from 'api/generated/types.gen';
import { useAppState } from 'lib/app-state';
import { clientTimezone, getLocalTimezoneAbbr } from 'lib/helpers/date-helpers';
import { FormattedPrice } from 'lib/helpers/formatted-price';
import { isSpecialOfferAccessDenied } from 'lib/helpers/special-offer-helpers';
import { sanitizeHtml } from 'lib/helpers/string-helpers';
import { useIsMobile } from 'lib/hooks/use-is-mobile';
import { useServerTimezone } from 'lib/hooks/use-server-timezone';
interface Props {
  data: SpecialOfferResponse;
  variant?: 'compact' | 'full';
  loading?: boolean;
  onClick?: () => void;
}
export const EventGroupOfferCard = ({
  data,
  variant = 'full',
  loading,
  onClick
}: Props) => {
  const intl = useIntl();
  const appState = useAppState();
  const {
    timezone: serverTimezone
  } = useServerTimezone();
  const isMobile = useIsMobile();
  if (!data.name || loading) return <Box sx={{
    display: 'flex',
    gap: 2
  }}>
        <Box>
          <Skeleton variant="formItem" width={80} height={80} />
        </Box>
        <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
          <Skeleton variant="text" width={80} height={28} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>;
  const isStartDateInFuture = data.startDate ? new Date(data.startDate) > new Date() : false;
  const noAccess = isSpecialOfferAccessDenied(data, !!appState.tokens);
  const accessDenied = data.isExpired || noAccess;
  const statusChip = isStartDateInFuture ? <Chip color="default" label={intl.formatMessage({
    id: "event.saleNotStarted",
    defaultMessage: [{
      "type": 0,
      "value": "Sale not started"
    }]
  })} size="small" variant="filled" /> : data.isExpired ? <Chip color="error" label={intl.formatMessage({
    id: "common.expired",
    defaultMessage: [{
      "type": 0,
      "value": "Expired"
    }]
  })} size="small" variant="filled" /> : noAccess ? <Chip color="error" label={intl.formatMessage({
    id: "event.accessDenied",
    defaultMessage: [{
      "type": 0,
      "value": "Access denied"
    }]
  })} size="small" variant="filled" /> : null;
  return <Box onClick={onClick} sx={[theme => ({
    borderColor: accessDenied || isStartDateInFuture ? 'transparent' : 'divider',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: {
      xs: `${theme.containerRadius.mobile}px`,
      sm: `${theme.containerRadius.main}px`
    },
    width: '100%',
    backgroundColor: accessDenied || isStartDateInFuture ? 'action.disabledBackground' : 'transparent'
  }), variant === 'compact' ? {
    p: 2
  } : {
    p: 3
  }, onClick ? {
    '&:hover': {
      backgroundColor: 'action.hover'
    }
  } : {
    '&:hover': {
      backgroundColor: 'transparent'
    }
  }, onClick ? {
    cursor: 'pointer'
  } : {
    cursor: 'default'
  }]}>
      <Box sx={{
      display: 'flex',
      gap: 2,
      width: '100%',
      alignItems: 'center'
    }}>
        <ImageBox imageUrl={data.eventGroup?.imageUrl || ''} altText={data.eventGroup?.name || ''} variant="thumbnail" sx={{
        width: '64px',
        bgcolor: 'grey.300'
      }} />
        <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
          <>
            <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
              <Typography variant="body1Medium" color={accessDenied ? 'text.disabled' : 'text.default'}>
                {data.eventGroup?.name}
              </Typography>
              {statusChip}
            </Box>
            <Typography variant="body3" color={accessDenied ? 'text.disabled' : 'text.secondary'}>
              {data.eventGroup?.hallName}
            </Typography>
          </>
          <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'baseline'
        }}>
            <Typography variant="body2" fontWeight={'500'} mt={0.5} color={accessDenied ? 'text.disabled' : 'text.default'}>
              <FormattedMessage id="common.eventNumDatesAvailable" defaultMessage={[{
              "type": 6,
              "value": "eventCount",
              "options": {
                "=0": {
                  "value": [{
                    "type": 0,
                    "value": "0 dates available"
                  }]
                },
                "=1": {
                  "value": [{
                    "type": 0,
                    "value": "1 date available"
                  }]
                },
                "other": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " dates available"
                  }]
                }
              },
              "offset": 0,
              "pluralType": "cardinal"
            }]} values={{
              eventCount: data.eventGroup?.datesAvailable
            }} />
            </Typography>
            {data.eventGroup?.lowestTicketPrice !== undefined && data.eventGroup?.lowestTicketPrice !== null && <Typography variant="body2" color={accessDenied ? 'text.disabled' : 'text.secondary'}>
                  {isMobile ? <FormattedMessage id="common.ticketsFromAmountShort" defaultMessage={[{
              "type": 0,
              "value": "from "
            }, {
              "type": 1,
              "value": "formattedAmount"
            }]} values={{
              formattedAmount: <FormattedPrice key="amount" amount={data.eventGroup?.lowestTicketPrice} />
            }} /> : <FormattedMessage id="common.ticketsFromAmount" defaultMessage={[{
              "type": 0,
              "value": "Tickets from "
            }, {
              "type": 1,
              "value": "formattedAmount"
            }]} values={{
              formattedAmount: <FormattedPrice key="amount" amount={data.eventGroup?.lowestTicketPrice} />
            }} />}
                </Typography>}
          </Box>
        </Box>
      </Box>
      {variant === 'full' && !accessDenied && !isStartDateInFuture && data.noteText && data.noteText !== '' && <>
            <Divider sx={{
        my: 3
      }} />
            <Typography variant="body1Medium">{data.noteHeading}</Typography>
            <Typography variant="body2" color="text.secondary" component="div" dangerouslySetInnerHTML={{
        __html: sanitizeHtml(data.noteText)
      }} sx={{
        '& > :last-child': {
          mb: 0
        }
      }} />
          </>}
      {isStartDateInFuture && <Stack direction="row" alignItems="center" mt={3} gap={1}>
          <ClockIcon sx={{
        width: 16
      }} />
          <Typography variant="body1">
            <FormattedMessage id="event.saleStartsOnDate" defaultMessage={[{
          "type": 0,
          "value": "Sale starts "
        }, {
          "type": 1,
          "value": "startDate"
        }, {
          "type": 0,
          "value": " at "
        }, {
          "type": 1,
          "value": "time"
        }]} values={{
          startDate: <FormattedDate value={data.startDate} dateStyle="medium" timeZone={clientTimezone} />,
          time: <>
                    <FormattedTime value={data.startDate} timeStyle="short" timeZone={clientTimezone} />
                    {clientTimezone !== serverTimezone ? ` ${getLocalTimezoneAbbr(data.startDate ?? '', appState.locale)}` : ''}
                  </>
        }} />
          </Typography>
        </Stack>}
    </Box>;
};